import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, JobTitle, getQueryKey } from '@feature/utils';

import * as api from './MinStdApi';
import { IndicatorItemOption, IndicatorItemRequest, IndicatorResult, MinStdReport, MinStdRequest } from './MinStdTypes';

const minStdKey = getQueryKey('min-std');

export function getMinStdOptions(userId: Id | null): UseSuspenseQueryOptions<MinStdReport | null> {
  return {
    queryFn: userId == null ? () => Promise.resolve(null) : () => api.get(userId),
    queryKey: minStdKey.one(userId),
  };
}

export function getMinStdAnnexTemplateOptions(annexNo: number) {
  return {
    queryFn: () => api.getMinStdAnnexTemplate(annexNo),
    queryKey: minStdKey.as(['template', annexNo]),
  };
}

export function getMinStdResultOptions(authorId: Id, jobTitle: JobTitle | null, startYear: number, endYear: number) {
  return {
    queryFn: () => api.getResult(authorId, jobTitle, startYear, endYear),
    queryKey: minStdKey.as(['author', authorId, jobTitle, startYear, endYear]),
  };
}

export function getMinStdAnnexListOptions() {
  return {
    queryFn: () => api.getAvailableAnnexes(),
    queryKey: minStdKey.as(['available-annex']),
  };
}

export function getMinStdIndicatorResultOptions(
  authorId: Id,
  indicatorCode: string,
  jobTitle: JobTitle | null,
  year: number | null,
): UseSuspenseQueryOptions<IndicatorResult> {
  return {
    queryFn: () => api.getIndicatorResult(authorId, indicatorCode, jobTitle, year),
    queryKey: minStdKey.as(['author', authorId, 'data', indicatorCode, 'items', year, jobTitle]),
  };
}

export function getMinStdIndicatorPossibleItemsOptions<T>(
  authorId: Id,
  indicatorCode: string,
  year: number | null,
): UseSuspenseQueryOptions<IndicatorItemOption<T>[]> {
  return {
    queryFn: () => api.getPossibleItemOptions<T>(authorId, indicatorCode, year),
    queryKey: minStdKey.as(['author', authorId, 'data', indicatorCode, 'possible-options', year]),
  };
}

export function useMinStdOpen() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: MinStdRequest) => api.open(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: minStdKey.root(),
      }),
  });
}

export function useMinStdAddIndicatorItem(authorId: Id, indicatorCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: IndicatorItemRequest) => api.addIndicatorItem(authorId, indicatorCode, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: minStdKey.root(),
      }),
  });
}

export function useMinStdAddAllIndicatorItems(authorId: Id, indicatorCode: string, year: number | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.addAllPossibleItemOptions(authorId, indicatorCode, year),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: minStdKey.root(),
      }),
  });
}

export function useMinStdEditIndicatorItem(authorId: Id, indicatorCode: string, itemId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: IndicatorItemRequest) => api.editIndicatorItem(authorId, indicatorCode, itemId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: minStdKey.root(),
      }),
  });
}

export function useMinStdDeleteIndicatorItem(authorId: Id, indicatorCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: Id) => api.deleteIndicatorItem(authorId, indicatorCode, itemId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: minStdKey.root(),
      }),
  });
}
